import axios from 'axios';
import { getIdToken } from '@/pages/MyAccount/components/firebaseIdToken';
import { extractApiError } from '@/utils/apiError';
import { describePostOpError } from '@/utils/postOp/errors';

// Same wording as the other token-gated screens (src/api/postOp.js, src/api/billing.js).
const NO_TOKEN_MESSAGE = "Couldn't confirm your sign-in. Reload the page and try again.";

export const FORMS_SETTINGS_TIMEOUT_MS = 30000;

/** The three blobs one PUT /save-forms-reminder-settings can write. */
export const FORMS_SETTINGS_SECTIONS = Object.freeze(['postOp', 'newPatient', 'medHx']);

/**
 * Forms Settings — the two staff calls behind the four-tab editor (plan docs/plans/2026-10-20-forms-settings-plan.md §2.3, D11;
 * teamio-serverless-api/forms-service/post-op-api.js getPostOpContext / savePostOpSettings):
 *
 *   PUT /get-forms-reminder-settings   { siteID }                       -> { settings, readiness, workflowOverlaps,                  STAFF: Bearer token
 *                                                                           newPatient { settings, readiness, blockReasons },
 *                                                                           medHx { settings|null, blockReasons },
 *                                                                           forms [{ formIDForWeb, name, formType }],
 *                                                                           packets [{ packetID, name, formIDs, isNewPatientPacket }],
 *                                                                           formsLoadFailed? }
 *   PUT /save-forms-reminder-settings  { siteID, section?, value }      -> { settings }                                              STAFF: Bearer token
 *        section 'postOp' (the default: the key is OMITTED, byte-compatible with src/api/postOp.js) | 'newPatient' | 'medHx'
 *
 * THE URLS ARE THE POST-OP ONES ON PURPOSE (the shared API Gateway is at its resource quota, and a path counts once): PUT, never POST —
 * POST on the same paths is the legacy Medical History modal's own function. Do not "tidy" these.
 *
 * Both calls FAIL CLOSED like api/postOp.js: no Firebase ID token => no request. The Authorization header is set explicitly, so the
 * global interceptor (src/plugins/authTokenInterceptor.js; a caller-set header wins, and PUT is always tagged anyway) does not touch it.
 *
 * ⛔ A SAVE NEVER TURNS ANYTHING ON BY ITSELF. `value.enabled` / `value.activated` go through here ONLY from a SendSwitchCard's confirm
 * (D15a). The editor's Save sends the content keys of the tabs the office edited, never the switch.
 *
 * REFUSALS (this is what the editor shows; the server is the gate, the WP0 rules are the hint):
 *   400 { code:'INVALID_SETTINGS', problems:[{ path, code, message }] }      newPatient / medHx
 *   400 { code:'ENABLE_BLOCKED',   reasons:[{ code, message }] }              newPatient / medHx - render the server's `message`
 *       (incl. the server-only WORKFLOW_CHECK_FAILED, CLINICS_NOT_SUPPORTED, CLINICS_CHECK_FAILED)
 *   422 { code:'INVALID_SETTINGS', errors:[{ field, message }] }              postOp (legacy shape, mapped onto `problems`)
 *   409 { code:'ENABLE_BLOCKED',   reason, message }                          postOp (legacy shape, mapped onto `reasons`)
 *
 * Every failure is rethrown as an Error whose `.info` (and the same keys copied onto the Error) is describeFormsSettingsError():
 *   { kind, status, code, problems, reasons, message }   `message` is the sentence to show.
 */

function presentable(value) {
  if (typeof value !== 'string') return '';
  const text = value.trim();
  return text && text.length <= 300 && text.indexOf('<') === -1 ? text : '';
}

function normalizeProblems(list) {
  return (Array.isArray(list) ? list : [])
    .filter((p) => p && typeof p === 'object')
    .map((p) => ({
      path: String(p.path || p.field || ''),
      code: typeof p.code === 'string' ? p.code : '',
      message: presentable(p.message) || 'Invalid value.',
    }));
}

function normalizeReasons(list, legacyReason, legacyMessage) {
  const reasons = (Array.isArray(list) ? list : [])
    .filter((r) => r && typeof r === 'object')
    .map((r) => ({ code: typeof r.code === 'string' ? r.code : '', message: presentable(r.message) }))
    .filter((r) => r.message !== '');
  if (reasons.length === 0 && presentable(legacyMessage)) {
    reasons.push({ code: typeof legacyReason === 'string' ? legacyReason : '', message: presentable(legacyMessage) });
  }
  return reasons;
}

/**
 * An axios-style failure -> what the editor shows.
 * @returns {{ kind:'invalid'|'blocked'|'network'|'auth'|'forbidden'|'server', status:number|null, code:string,
 *             problems:Array<{path:string,code:string,message:string}>, reasons:Array<{code:string,message:string}>, message:string }}
 */
export function describeFormsSettingsError(err, fallback) {
  const res = err && err.response;
  const status = res && typeof res.status === 'number' ? res.status : null;
  const data = res && res.data && typeof res.data === 'object' ? res.data : null;
  const code = data && typeof data.code === 'string' ? data.code : '';
  const out = { kind: 'server', status, code, problems: [], reasons: [], message: '' };

  if (code === 'INVALID_SETTINGS' && (status === 400 || status === 422)) {
    out.kind = 'invalid';
    out.problems = normalizeProblems(data.problems !== undefined ? data.problems : data.errors);
    out.message = out.problems.length > 0 ? out.problems[0].message : 'Some of these settings are not valid.';
    return out;
  }
  if (code === 'ENABLE_BLOCKED' && (status === 400 || status === 409)) {
    out.kind = 'blocked';
    out.reasons = normalizeReasons(data.reasons, data.reason, data.message);
    out.message = out.reasons.length > 0 ? out.reasons[0].message : 'This can’t be turned on yet.';
    return out;
  }

  // Network failures, an expired sign-in, a forbidden site and every 5xx keep post-op's wording (a 5xx body is a bare driver string
  // that must never reach an office). A 4xx the server explained in its own words goes through extractApiError, which drops HTML bodies
  // and internal identifiers and falls back to the call site's sentence.
  const info = describePostOpError(err, fallback);
  out.kind = info.kind;
  out.message = info.kind === 'server' && status !== null && status < 500 ? extractApiError(err, fallback) : info.message;
  return out;
}

/** An Error carrying describeFormsSettingsError()'s result as `.info` and, copied, as `.kind .status .code .problems .reasons`. */
export function formsSettingsApiError(info) {
  const err = new Error(info.message);
  err.name = 'FormsSettingsApiError';
  err.info = info;
  err.kind = info.kind;
  err.status = info.status;
  err.code = info.code;
  err.problems = info.problems;
  err.reasons = info.reasons;
  return err;
}

async function authHeaders(auth) {
  const idToken = await getIdToken(auth);
  if (!idToken) {
    throw formsSettingsApiError({ kind: 'auth', status: null, code: 'NO_TOKEN', problems: [], reasons: [], message: NO_TOKEN_MESSAGE });
  }
  return { Authorization: `Bearer ${idToken}` };
}

/** The whole Forms Settings context in ONE call (the editor loads it once and hands slices to the tabs). */
export async function fetchFormsSettingsContext({ auth, siteID } = {}) {
  const headers = await authHeaders(auth);
  try {
    const { data } = await axios.put('/get-forms-reminder-settings', { siteID }, { headers, timeout: FORMS_SETTINGS_TIMEOUT_MS, crossdomain: true });
    return data;
  } catch (err) {
    throw formsSettingsApiError(describeFormsSettingsError(err, 'Couldn’t load Forms Settings. Please try again.'));
  }
}

/**
 * Save ONE section: `value` holds just the keys that change (the server overlays them on the stored blob). Resolves with the
 * server's `{ settings }`. An unknown section is refused here, before any request.
 */
export async function saveFormsSettingsSection({ auth, siteID, section, value } = {}) {
  if (FORMS_SETTINGS_SECTIONS.indexOf(section) === -1) {
    throw formsSettingsApiError({
      kind: 'server', status: null, code: 'BAD_SECTION', problems: [], reasons: [], message: `Unknown Forms Settings section: ${String(section)}`,
    });
  }
  const headers = await authHeaders(auth);
  const body = section === 'postOp' ? { siteID, value } : { siteID, section, value };
  try {
    const { data } = await axios.put('/save-forms-reminder-settings', body, { headers, timeout: FORMS_SETTINGS_TIMEOUT_MS, crossdomain: true });
    return data;
  } catch (err) {
    throw formsSettingsApiError(describeFormsSettingsError(err, 'Couldn’t save your changes. Please try again.'));
  }
}
